import { URLConstructor } from '../../../NonReact/utils/URLBuilder'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { handleError } from '../../../NonReact/common/API/REST/RESTErrorHandler'
import { RESTAPIError } from '../../../NonReact/common/API/REST/RESTAPIError'
import { redirectIfSessionExpired } from '../../../NonReact/utils/SessionUtils'

/**
 * Sends a POST multipart/form-data request to the specified URL.
 *
 * @template T - The expected response type.
 * @param {URLConstructor} url - The URL to send the request to.
 * @param {Record<string, string | Blob>} data - The data to be sent in the request body.
 * @param {RequestInit} [fetchOptions] - Optional additional fetch options.
 * @returns {Promise<Nullable<T>>} - A promise that resolves to the response data or null if an error occurs.
 */
export const requestMultiPart = <T extends unknown>(
    url: URLConstructor,
    data: Record<string, string | Blob>,
    fetchOptions?: RequestInit
): Promise<Nullable<T>> => {
    const formData: FormData = new FormData()

    // Add the data to the form data.
    Object.entries(data).forEach(([key, value]: [string, string | Blob]): void => formData.append(key, value))

    // Construct the fetch options.
    const options: RequestInit = {
        method: RESTMethods.POST,
        body: formData,
        credentials: 'include',
        ...fetchOptions
    }

    // Fetch the data from the server.
    return new Promise((resolve, reject): void => {
        const rejectIfNotExpiredSession = (error: RESTAPIError): void => {
            if (!redirectIfSessionExpired(error)) reject(error)
            // Else: page is unloading — don't reject the promise.
        }

        fetch(url.constructURL(), options)
            .then((response: Response): void => {
                if (response.ok) {
                    resolve(response.json())
                } else {
                    handleError(response).then(rejectIfNotExpiredSession).catch(reject)
                }
            })
            .catch((e: Response): Promise<void> => handleError(e).then(rejectIfNotExpiredSession).catch(reject))
    })
}
